
import { getQuotes } from '@/api';
import Button from '@/components/UI/button/Button.vue';
import * as routeNames from '@/router/routeNames';
import * as Sentry from '@sentry/vue';

import { QuoteListItem } from '@/models/quote';
import { format, parseISO } from 'date-fns';
import { Component, Vue } from 'vue-property-decorator';
import { notificationErrorText } from '@/utils/PortalConstants';

@Component({
  components: {
    Button,
  },
})
export default class QuoteList extends Vue {
  private loading = false;
  private quotes: QuoteListItem[] = [];
  private totalCount = 0;
  private search = '';
  private statusFilter = '';

  private pagination = {
    page: 1,
    perPage: 20,
    totalPages: 0,
  };

  private perPageOptions = [10, 20, 50, 100];

  private statusOptions = [
    { text: 'All', value: '' },
    { text: 'Draft', value: 'draft' },
    { text: 'Sent', value: 'sent' },
    { text: 'Accepted', value: 'accepted' },
    { text: 'Booked', value: 'booked' },
    { text: 'Expired', value: 'expired' },
    { text: 'Cancelled', value: 'cancelled' },
  ];

  private tableHeaders = [
    { text: '#', value: 'id', width: '70px' },
    { text: 'Reference', value: 'reference' },
    { text: 'Customer', value: 'customer_name' },
    { text: 'Date', value: 'created_at', width: '130px' },
    { text: 'Total (inc GST)', value: 'total_fee_inc_gst', align: 'end', width: '150px' },
    { text: 'Status', value: 'status', width: '120px' },
    { text: 'Actions', value: 'actions', sortable: false, width: '80px' },
  ];

  async mounted() {
    await this.fetchQuotes();
  }

  private async fetchQuotes() {
    this.loading = true;
    try {
      const response = await getQuotes(this.pagination.page, this.pagination.perPage, this.statusFilter || undefined);
      this.quotes = response.results;
      this.totalCount = response.count;
      this.pagination.totalPages = Math.ceil(response.count / this.pagination.perPage);
    } catch (err: any) {
      Sentry.captureException(err);
      this.$notify({
        group: 'network',
        text: notificationErrorText,
        type: 'warn',
        duration: 3000,
      });
    } finally {
      this.loading = false;
    }
  }

  private onPageChange() {
    this.fetchQuotes();
  }

  private onPerPageChange() {
    this.pagination.page = 1;
    this.fetchQuotes();
  }

  private onFilterChange() {
    this.pagination.page = 1;
    this.fetchQuotes();
  }

  private formatDate(dateStr: string): string {
    try {
      return format(parseISO(dateStr), 'dd MMM yy');
    } catch {
      return dateStr;
    }
  }

  private statusColor(status: string): string {
    const colors: Record<string, string> = {
      draft: 'grey',
      sent: 'blue',
      accepted: 'green',
      booked: 'purple',
      expired: 'orange',
      cancelled: 'red',
    };
    return colors[status] || 'grey';
  }

  private capitalize(str: string): string {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
  }

  private goToDetail(item: QuoteListItem) {
    this.$router.push({ name: routeNames.DETAILED_QUOTE, params: { id: String(item.id) } });
  }
}
